import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, Loader2, ArrowRight, Check, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fireGoogleAdsConversion } from "@/lib/googleAds";

const REF_STORAGE_KEY = "agentflow_ref_code";

export default function Register() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // Referral code resolution: ?ref=XXX in URL wins, otherwise fall back
  // to a previously stashed code (so users who land via a share-link but
  // sign up days later still credit the referrer). Validated server-side.
  const [refCode, setRefCode] = useState("");

  useEffect(() => {
    const urlRef = (searchParams.get("ref") || "").trim().toUpperCase();
    if (urlRef) {
      setRefCode(urlRef);
      try { localStorage.setItem(REF_STORAGE_KEY, urlRef); } catch { /* no-op */ }
      return;
    }
    try {
      const stashed = (localStorage.getItem(REF_STORAGE_KEY) || "").trim().toUpperCase();
      if (stashed) setRefCode(stashed);
    } catch { /* no-op */ }
  }, [searchParams]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const body = { name, email, password };
      if (refCode) body.referral_code = refCode;
      const { data } = await api.post("/auth/register", body);
      setUser(data);
      // Fire Google Ads "Sign-up" conversion — this is the "thank-you /
      // success page" moment Google Ads asks for. Using the account's
      // user_id as the transaction_id so duplicate fires (e.g. if the
      // component remounts) don't double-count in Google's dashboard.
      fireGoogleAdsConversion("signup", {
        transactionId: data?.user_id || data?.email || undefined,
      });
      // Don't drop the ref code yet — keep it around until credited so we
      // can show a "your friend will get +3 scans on your first scan"
      // banner if needed. Safe to leave; back-end is idempotent.
      toast.success("Welcome aboard!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error("Couldn't create account", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex relative bg-slate-900 text-white p-12 flex-col justify-between overflow-hidden">
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-blue-500/30 blur-3xl" />
        <div className="absolute -bottom-32 -left-12 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />
        <Link to="/" className="relative flex items-center gap-2">
          <span className="h-9 w-9 rounded-md bg-blue-600 inline-flex items-center justify-center">
            <ShieldCheck className="h-5 w-5" strokeWidth={2.5} />
          </span>
          <span className="font-display font-semibold text-xl">AgentFlow<span className="text-blue-400">.</span></span>
        </Link>
        <div className="relative max-w-md space-y-6">
          <h1 className="font-display text-4xl font-semibold leading-tight">
            Start scanning in{" "}
            <span className="text-cyan-300">under two minutes.</span>
          </h1>
          <ul className="space-y-3 text-sm text-slate-200">
            {[
              "5 free document scans every month",
              "Live regulation monitoring across GDPR & CCPA",
              "Audit-ready PDF exports — branded with your name",
              "No credit card required",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300 shrink-0">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
                {t}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-xs text-slate-400">SOC 2 Type II · GDPR · ISO 27001 ready</p>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <span className="h-8 w-8 rounded-md bg-blue-600 inline-flex items-center justify-center text-white">
              <ShieldCheck className="h-4 w-4" />
            </span>
            <span className="font-display font-semibold text-lg">AgentFlow<span className="text-blue-600">.</span></span>
          </Link>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900">Create your account</h2>
          <p className="mt-1.5 text-sm text-slate-600">
            Already have one?{" "}
            <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">Sign in</Link>
          </p>

          <form onSubmit={onSubmit} className="mt-7 space-y-3" data-testid="register-form">
            {refCode && (
              <div
                className="rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2.5 flex items-start gap-2"
                data-testid="register-referral-banner"
              >
                <Gift className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
                <div className="text-xs text-emerald-900">
                  You've been invited! You'll both get{" "}
                  <strong className="font-semibold">+3 bonus scans</strong>{" "}
                  after your first contract scan.
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-11" data-testid="register-name" />
            </div>
            <div>
              <Label htmlFor="email">Work email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-11" data-testid="register-email" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 h-11" data-testid="register-password" />
              <p className="mt-1 text-xs text-slate-500">At least 8 characters.</p>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-lg bg-blue-600 hover:bg-blue-700 text-white" data-testid="register-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Create account <ArrowRight className="ml-2 h-4 w-4" /></>)}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
